import { fmt } from "../lib/money.js";

export interface ProjectionChartProps {
  /** Projection values in cents, one per month. */
  projection: number[];
  /** Target value in cents. */
  target: number;
  color: string;
}

export function ProjectionChart({ projection, target, color }: ProjectionChartProps) {
  const W = 800;
  const H = 240;
  const PAD = { l: 60, r: 20, t: 20, b: 40 };
  const safeProjection = projection.length > 0 ? projection : [0];
  const max = Math.max(target, ...safeProjection) * 1.05 || 1;
  const x = (i: number) =>
    PAD.l + (i / Math.max(1, safeProjection.length - 1)) * (W - PAD.l - PAD.r);
  const y = (v: number) => H - PAD.b - (v / max) * (H - PAD.t - PAD.b);

  const path = safeProjection.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");
  const area = `${path} L ${x(safeProjection.length - 1)} ${H - PAD.b} L ${x(0)} ${H - PAD.b} Z`;

  // find month when goal hit
  const hitMonth = safeProjection.findIndex((v) => v >= target);
  const hitValue = hitMonth >= 0 ? (safeProjection[hitMonth] ?? 0) : 0;

  const labelMonths = [0, 6, 12, 18, 24, 30, 36].filter((i) => i < safeProjection.length);

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: "block" }}>
      {/* grid */}
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <line
          key={f}
          x1={PAD.l}
          x2={W - PAD.r}
          y1={PAD.t + f * (H - PAD.t - PAD.b)}
          y2={PAD.t + f * (H - PAD.t - PAD.b)}
          stroke="var(--hairline-soft)"
        />
      ))}
      {/* target line */}
      <line
        x1={PAD.l}
        x2={W - PAD.r}
        y1={y(target)}
        y2={y(target)}
        stroke={color}
        strokeDasharray="4 4"
        opacity="0.6"
      />
      <text x={W - PAD.r - 4} y={y(target) - 6} fontSize="11" fill={color} textAnchor="end">
        Target: {fmt(target, { showCents: false })}
      </text>

      {/* area fill */}
      <path d={area} fill={color} opacity="0.12" />
      <path d={path} fill="none" stroke={color} strokeWidth="2.5" />

      {/* hit marker */}
      {hitMonth > 0 && (
        <>
          <circle cx={x(hitMonth)} cy={y(hitValue)} r="6" fill={color} />
          <circle cx={x(hitMonth)} cy={y(hitValue)} r="12" fill={color} opacity="0.25" />
        </>
      )}

      {/* axes labels */}
      {labelMonths.map((i) => (
        <text
          key={i}
          x={x(i)}
          y={H - PAD.b + 18}
          fontSize="11"
          fill="var(--ink-mute)"
          textAnchor="middle"
        >
          {i === 0 ? "now" : `${i}mo`}
        </text>
      ))}
      <text x={PAD.l - 8} y={y(0) + 4} fontSize="11" fill="var(--ink-mute)" textAnchor="end">
        $0
      </text>
      <text x={PAD.l - 8} y={y(max) + 10} fontSize="11" fill="var(--ink-mute)" textAnchor="end">
        {fmt(Math.round(max), { showCents: false })}
      </text>
    </svg>
  );
}
